import { useState } from 'react';
import { useQuery } from 'react-query';
/** Components */
import { DefaultTable } from 'Components/DefaultTable';
import { PageHelper } from 'Components/PageHelper';
import { Container } from '../../Components/Container';
import { Information } from 'Components/Information';
import { tableHeader } from './table-header.js';
import { ModalConfirmDelete } from 'Components/ModalConfirmDelete';
import { PageLoaderList } from 'Components/PageLoader/List';
/** Services */
import { getSystemsShortPaged } from 'services/systems-service';
/** State Managers */
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
/** Third Lib Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { NoData } from 'Components/NoData';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Box
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { getUsersName } from 'services/users-service';
import { Card } from 'Components/Card';
import { Column, Row } from 'Components/Grid';
import { CustomTextField } from 'Components/Form/styles';
import { InputLoader } from 'Components/PageLoader/Input';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import { callDeleteAction, callArchiveAction, callUnarchiveAction } from './actions';
import { ModalConfirm } from 'Components/ModalConfirm';

export const Systems = () => {
  const selectedTenant = useSelector(useAuthTenant);
  const SwalReact = withReactContent(Swal);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openArchiveModal, setOpenArchiveModal] = useState(false);
  const [openUnarchiveModal, setOpenUnarchiveModal] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemToArchive, setItemToArchive] = useState(null);
  const [itemToUnarchive, setItemToUnarchive] = useState(null);
  const [page, setPage] = useState(1);

  const { register, control, reset, watch, getValues } = useForm({
    defaultValues: {
      name: '',
      criticity: '',
      developedInHouse: 'Todos',
      saas: 'Todos',
      user: '',
      archived: false,
    },
  });

  const getContentSearch = () => {
    const dados = getValues();
    let queryString = `&archived=${dados.archived}&developedInHouse=${dados.developedInHouse}&saas=${dados.saas}`;
    if (dados.name !== '') {
      queryString += `&name=${dados.name}`;
    }
    if (dados.user !== '') {
      queryString += `&user=${dados.user}`;
    }
    if (dados.criticity !== '') {
      queryString += `&criticity=${dados.criticity}`;
    }

    return queryString;
  };

  const { isLoading: userLoading, data: users } = useQuery(['getUsers'], async () => {
    if (selectedTenant.slug === undefined || selectedTenant.slug === null) return [];
    const result = await getUsersName(selectedTenant.slug);
    return result?.data.length > 0 ? result?.data : [];
  });

  const {
    isLoading,
    error,
    data: pagedSystems,
  } = useQuery(['getSystems', refetch, page], async () => {
    if (selectedTenant.slug === undefined || selectedTenant.slug === null) return [];

    const queryContent = getContentSearch();
    const result = await getSystemsShortPaged(selectedTenant.slug, page, 15, queryContent);
    if (result?.data.data.length > 0) {
      return result?.data;
    }
    return {
      data: [],
      page,
      totalPages: 0,
    };
  });

  const onDelete = item => {
    setItemToDelete(item);
    setOpenDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setItemToDelete(null);
  };

  const onArchive = item => {
    setItemToArchive(item);
    setOpenArchiveModal(true);
  };

  const onCloseArchiveModal = () => {
    setOpenArchiveModal(false);
    setItemToArchive(null);
  };

  const onUnarchive = item => {
    setItemToUnarchive(item);
    setOpenUnarchiveModal(true);
  };

  const onCloseUnarchiveModal = () => {
    setOpenUnarchiveModal(false);
    setItemToUnarchive(null);
  };

  const onConfirmDeleteModal = async () => {
    try {
      await callDeleteAction(itemToDelete, onCloseDeleteModal, refetch, setRefetch, SwalReact);
    } catch (error) {
      onCloseDeleteModal();
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const onConfirmArchiveModal = async () => {
    try {
      await callArchiveAction(itemToArchive, onCloseArchiveModal, refetch, setRefetch, SwalReact);
    } catch (error) {
      onCloseArchiveModal();
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const onConfirmUnarchiveModal = async () => {
    try {
      await callUnarchiveAction(itemToUnarchive, onCloseUnarchiveModal, refetch, setRefetch, SwalReact);
    } catch (error) {
      onCloseUnarchiveModal();
      SwalReact.fire({
        title: 'Erro!',
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  return (
    <Container title="Sistemas" breadcrumb="Mapeamento / Sistemas" showButtonAdd linkPage="/systems" dataTour="page-title" dataTourAdd="add-button">
      <div data-tour="info-card">
        <Information
          title={'Sistemas'}
          description={
            <p className="m-0">
              Diversas ferramentas são utilizadas no nosso dia a dia para a execução das nossas atividades, lembrando que
              esses sistemas são ferramentas online/SaaS (CRM, E-mail Marketing, Suítes de Produtividade, etc) ou
              ferramentas desenvolvidas internamente.
            </p>
          }
          style={{
            marginTop: '24px',
          }}
        />
      </div>
      {!error && (
        <>
          <Card className="mb-2 mt-3" data-tour="filter-card">
            <Row className="mb-3">
              <Column xs={12} md={6} className="mb-2 mb-md-0">
                <div data-tour="filter-name" style={{ width: '100%' }}>
                  <CustomTextField
                    {...register('name')}
                    label="Nome"
                  sx={{
                    minHeight: '40px',
                    maxHeight: '40px',
                    margin: '0 auto',
                    width: '100%',
                    ' > div': {
                      minHeight: '40px',
                    },
                  }}
                  InputLabelProps={{ shrink: true }}
                />                </div>              </Column>
              <Column xs={12} md={3} className="mb-2 mb-md-0">
                <div data-tour="filter-user" style={{ width: '100%' }}>
                  {!userLoading && (
                    <Controller
                    control={control}
                    name="user"
                    render={({ field: { onChange, value } }) => (
                      <FormControl fullWidth>
                        <InputLabel id="user-label" shrink>
                          Responsável
                        </InputLabel>
                        <Select
                          labelId="user-label"
                          id="user"
                          value={value}
                          label="Responsável"
                          onChange={onChange}
                          notched
                          sx={{
                            minHeight: '40px',
                            maxHeight: '40px',
                            margin: '0 auto',
                            width: '100%',
                          }}
                        >
                          <MenuItem value="" key={-1}>
                            Selecione um responsável
                          </MenuItem>
                          {users
                            ?.map(user => user.name)
                            .map((item, index) => (
                              <MenuItem value={item} key={`user-${index}`}>
                                {item}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                )}
                {userLoading && (
                  <FormControl fullWidth sx={{ margin: '0 auto' }}>
                    <InputLabel id="user-label" shrink={true}>
                      Responsável
                    </InputLabel>
                    <InputLoader height={'40px'} />
                  </FormControl>
                )}
                </div>
              </Column>
              <Column xs={12} md={3} className="mb-2 mb-md-0">
                <div data-tour="filter-criticity" style={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="criticity"
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="criticity-label" shrink>
                        Criticidade
                      </InputLabel>
                      <Select
                        labelId="criticity-label"
                        id="criticity"
                        value={value}
                        label="Criticidade"
                        onChange={onChange}
                        notched
                        sx={{
                          minHeight: '40px',
                          maxHeight: '40px',
                          margin: '0 auto',
                          width: '100%',
                        }}
                        renderValue={(selected) => {
                          if (!selected) return 'Selecione uma criticidade';
                          
                          const getCriticityColor = (level) => {
                            const colors = {
                              '1': '#22c55e', // Verde
                              '2': '#84cc16', // Verde claro
                              '3': '#eab308', // Amarelo
                              '4': '#f97316', // Laranja
                              '5': '#ef4444'  // Vermelho
                            };
                            return colors[level] || '#6b7280';
                          };
                          
                          const getCriticityLabel = (level) => {
                            const labels = {
                              '1': '1 - Muito Baixa',
                              '2': '2 - Baixa',
                              '3': '3 - Média',
                              '4': '4 - Alta',
                              '5': '5 - Muito Alta'
                            };
                            return labels[level] || level;
                          };
                          
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div 
                                style={{
                                  width: '12px',
                                  height: '12px',
                                  borderRadius: '50%',
                                  backgroundColor: getCriticityColor(selected),
                                  border: '1px solid rgba(0,0,0,0.1)'
                                }}
                              />
                              <span>{getCriticityLabel(selected)}</span>
                            </div>
                          );
                        }}
                      >
                        <MenuItem value="" key={-1}>
                          Selecione uma criticidade
                        </MenuItem>
                        {['1', '2', '3', '4', '5'].map((item, index) => {
                          const getCriticityColor = (level) => {
                            const colors = {
                              '1': '#22c55e', // Verde
                              '2': '#84cc16', // Verde claro
                              '3': '#eab308', // Amarelo
                              '4': '#f97316', // Laranja
                              '5': '#ef4444'  // Vermelho
                            };
                            return colors[level] || '#6b7280';
                          };
                          
                          const getCriticityLabel = (level) => {
                            const labels = {
                              '1': '1 - Muito Baixa',
                              '2': '2 - Baixa',
                              '3': '3 - Média',
                              '4': '4 - Alta',
                              '5': '5 - Muito Alta'
                            };
                            return labels[level] || level;
                          };
                          
                          return (
                            <MenuItem value={item} key={index}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div 
                                  style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: getCriticityColor(item),
                                    border: '1px solid rgba(0,0,0,0.1)'
                                  }}
                                />
                                <span>{getCriticityLabel(item)}</span>
                              </div>
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  )}
                />
                </div>
              </Column>
            </Row>
            <Row className="mb-2">
              <Column xs={12} md={3} className="mb-2 mb-md-0">
                <div data-tour="filter-developed" style={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="developedInHouse"
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="developed-in-house-label" shrink>
                        Desenvolvido internamente
                      </InputLabel>
                      <Select
                        labelId="developed-in-house-label"
                        id="developedInHouse"
                        value={value}
                        label="Desenvolvido internamente"
                        onChange={onChange}
                        notched
                        sx={{
                          minHeight: '40px',
                          maxHeight: '40px',
                          margin: '0 auto',
                          width: '100%',
                        }}
                      >
                        {['Todos', 'Sim', 'Não'].map(item => (
                          <MenuItem value={item} key={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                </div>
              </Column>
              <Column xs={12} md={2} className="mb-2 mb-md-0">
                <div data-tour="filter-saas" style={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="saas"
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel id="saas-label" shrink>
                        SAAS
                      </InputLabel>
                      <Select
                        labelId="saas-label"
                        id="saas"
                        value={value}
                        label="SAAS"
                        onChange={onChange}
                        notched
                        sx={{
                          minHeight: '40px',
                          maxHeight: '40px',
                          margin: '0 auto',
                          width: '100%',
                        }}
                      >
                        {['Todos', 'Sim', 'Não'].map(item => (
                          <MenuItem value={item} key={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
                </div>
              </Column>
              <Column xs={12} md={1} className="mb-2 mb-md-0">
                <div data-tour="filter-archived" style={{ width: '100%' }}>
                  <Controller
                    control={control}
                    name="archived"
                  render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                      value={value}
                      control={<Checkbox checked={value} onChange={onChange} name="archived" />}
                      label="Arquivado"
                      sx={{ marginLeft: 0, width: 'fit-content' }}
                    />
                  )}
                />
                </div>
              </Column>
              <Column xs={12} md={2} className="mb-2 mb-md-0">
                <Box display="flex" gap={1} data-tour="filter-buttons">
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: '12px',
                      padding: '10px 24px',
                      textTransform: 'none',
                      width: '100%',
                      '@media (min-width: 768px)': {
                        width: 'fit-content'
                      }
                    }}
                    onClick={() => {
                      reset();
                      setRefetch(!refetch);
                    }}
                  >
                    Limpar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      borderRadius: '12px',
                      padding: '10px 24px',
                      textTransform: 'none',
                      width: '100%',
                      '@media (min-width: 768px)': {
                        width: 'fit-content'
                      }
                    }}
                    endIcon={<SearchOutlinedIcon />}
                    disabled={watch('typeFilter') === ''}
                    onClick={() => {
                      setRefetch(!refetch);
                    }}
                  >
                    Filtrar
                  </Button>
                </Box>
              </Column>

            </Row>
          </Card>
        </>
      )}
      {isLoading && <PageLoaderList></PageLoaderList>}
      {error && <div>Ocorreu um erro ...</div>}
      {!isLoading && !error && pagedSystems.data?.length === 0 && (
        <NoData description={'Não há Sistemas para exibir'} />
      )}
      {!isLoading && !error && pagedSystems.data?.length > 0 && (
        <div className="container">
          <div data-tour="table">
            <DefaultTable
            rows={tableHeader}
            cells={pagedSystems.data}
            onClickDelete={onDelete}
            onClickArchive={onArchive}
            onClickUnarchive={onUnarchive}
            showArchive
            link="systems"
          />
          </div>
          <div data-tour="pagination">
            <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginTop: '16px' }}>
              <Pagination
              count={pagedSystems.totalPages}
              onChange={(_, selectedPage) => {
                setPage(selectedPage);
              }}
              color="primary"
              page={page}
              showFirstButton
              showLastButton
            />
          </Stack>
          </div>
          <ModalConfirmDelete
            description={
              <>
                Tem certeza que deseja excluir <strong>{` ${itemToDelete?.name} `}</strong> como sistema?
              </>
            }
            open={openDeleteModal}
            onClose={onCloseDeleteModal}
            onConfirm={onConfirmDeleteModal}
          />
          <ModalConfirm
            description={
              <>
                Tem certeza que deseja arquivar o Sistema <strong>{` ${itemToArchive?.name}`}</strong>?
              </>
            }
            open={openArchiveModal}
            onClose={onCloseArchiveModal}
            onConfirm={onConfirmArchiveModal}
          />
          <ModalConfirm
            description={
              <>
                Tem certeza que deseja desarquivar o Sistema
                <strong>{` ${itemToUnarchive?.name}`}</strong>?
              </>
            }
            open={openUnarchiveModal}
            onClose={onCloseUnarchiveModal}
            onConfirm={onConfirmUnarchiveModal}
          />
        </div>
      )}
      <PageHelper steps={tourSteps} tourKey="systems-list" />
    </Container>
  );
};

// Tour steps
const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo à página de Sistemas! Aqui você pode visualizar, filtrar e gerenciar todos os sistemas da organização.',
    placement: 'center',
    disableBeacon: true,
  },
  {
    target: '[data-tour="page-title"]',
    content: 'Clique aqui no título para voltar à lista de sistemas.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="add-button"]',
    content: 'Clique no botão "+" para adicionar um novo sistema à sua organização.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="info-card"]',
    content: 'Este card explica que sistemas incluem ferramentas online/SaaS e sistemas desenvolvidos internamente.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="filter-card"]',
    content: 'Use este painel de filtros para encontrar sistemas específicos.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="filter-name"]',
    content: 'Filtre sistemas pelo nome.',
  },
  {
    target: '[data-tour="filter-user"]',
    content: 'Filtre sistemas por responsável.',
  },
  {
    target: '[data-tour="filter-criticity"]',
    content: 'Filtre por criticidade: de 1 (Muito Baixa) a 5 (Muito Alta). As cores ajudam na identificação visual.',
  },
  {
    target: '[data-tour="filter-developed"]',
    content: 'Filtre por sistemas desenvolvidos internamente ou adquiridos de terceiros.',
  },
  {
    target: '[data-tour="filter-saas"]',
    content: 'Filtre por sistemas que são ou não SaaS (Software as a Service).',
  },
  {
    target: '[data-tour="filter-archived"]',
    content: 'Marque esta opção para visualizar sistemas arquivados.',
  },
  {
    target: '[data-tour="filter-buttons"]',
    content: 'Use "Limpar" para resetar todos os filtros ou "Filtrar" para aplicar os critérios selecionados.',
  },
  {
    target: '[data-tour="table"]',
    content: 'Esta tabela exibe todos os sistemas. Você pode visualizar detalhes, editar, arquivar ou excluir cada sistema.',
  },
  {
    target: '[data-tour="pagination"]',
    content: 'Use a paginação para navegar entre as páginas de sistemas.',
  },
  {
    target: 'body',
    content: 'Tour concluído! Agora você conhece todas as funcionalidades da página de Sistemas.',
    placement: 'center',
  },
];
